import { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faDog } from '@fortawesome/pro-solid-svg-icons/faDog';
import { faCat } from '@fortawesome/pro-solid-svg-icons/faCat';
import { faPaw } from '@fortawesome/pro-solid-svg-icons/faPaw';
import { faCamera } from '@fortawesome/pro-solid-svg-icons/faCamera';
import { faThumbsUp } from '@fortawesome/pro-solid-svg-icons/faThumbsUp';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';
import { faFlag } from '@fortawesome/pro-solid-svg-icons/faFlag';
import { faShare } from '@fortawesome/pro-solid-svg-icons/faShare';
import { faShareNodes } from '@fortawesome/pro-solid-svg-icons/faShareNodes';
import { faBolt } from '@fortawesome/pro-solid-svg-icons/faBolt';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons/faCircleInfo';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { faSearch } from '@fortawesome/pro-solid-svg-icons/faSearch';
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser';
import { faIdCard } from '@fortawesome/pro-solid-svg-icons/faIdCard';
import { faComments } from '@fortawesome/pro-solid-svg-icons/faComments';
import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons/faPenToSquare';
import { faCreditCard } from '@fortawesome/pro-solid-svg-icons/faCreditCard';
import { faShieldCheck } from '@fortawesome/pro-solid-svg-icons/faShieldCheck';
import { faBuilding } from '@fortawesome/pro-solid-svg-icons/faBuilding';
import { faHeartPulse } from '@fortawesome/pro-solid-svg-icons/faHeartPulse';
import { faHouseUser } from '@fortawesome/pro-solid-svg-icons/faHouseUser';
import { faRotate } from '@fortawesome/pro-solid-svg-icons/faRotate';
import { faUserGroup } from '@fortawesome/pro-solid-svg-icons/faUserGroup';
import { faMessage } from '@fortawesome/pro-solid-svg-icons/faMessage';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { faHandshake } from '@fortawesome/pro-solid-svg-icons/faHandshake';
import { faAward } from '@fortawesome/pro-solid-svg-icons/faAward';
import { faClock } from '@fortawesome/pro-solid-svg-icons/faClock';
import { faFacebook } from '@fortawesome/free-brands-svg-icons/faFacebook';

import {
  Parent as BaseParent,
  ParentService,
  JobService,
  SitterService,
  Sitter,
  LanguageService,
  Language,
  AffiliationService,
  LocalGroupService,
  SchoolService,
  ParentAffiliationService,
  ParentKidsService,
  ParentLanguagesService,
  ParentLocalGroupsService,
  ParentSchoolsService,
  SpecialExperience,
  SpecialExperiencesService,
  AxiosError,
  HeicConvertService,
  ContentFlagService,
  PROFILE_FLAG_REASONS,
} from 'us-web-services';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import {
  formatDateRange,
  getMonthDayString,
} from 'us-react-components/dist/utils/date';
import { fullName } from 'us-react-components/dist/utils/string';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { ModalName } from '../../modals/constants';
import { useModal } from '../../modals/modalContext';

interface ParentBadge {
  type?: string;
  label?: string;
  description?: string;
  active?: boolean;
  url?: string;
  icon?: string;
}

interface ParentExtras extends Omit<BaseParent, 'relationship'> {
  badges?: ParentBadge[];
  languages?: Array<{ id?: number; languageId?: number; name?: string }>;
  emergencyName?: string;
  emergencyPhone?: string;
  pediatricianName?: string;
  pediatricianPhone?: string;
  userStats?: { lastAccessLabel?: string };
  relationship?: {
    blocked?: boolean;
    bookingCount?: number;
    blockedId?: number | null;
  };
}

interface JobBookingShape {
  start?: string;
  end?: string;
  rate?: number;
  paymentType?: string;
  numberOfChildren?: number;
  kidsAges?: string;
  description?: string;
  typeId?: number;
  address?: { city?: string; state?: string };
}

interface Job {
  id?: number;
  recurring?: boolean;
  availability?: string;
  booking?: JobBookingShape;
}

const BOOKING_TYPE_CODES: Record<number, string> = {
  1: 'childCare',
  2: 'petCare',
  3: 'seniorCare',
  4: 'householdServices',
};

const getJobTitle = (job: Job): string => {
  if (job.recurring) {
    const start = getMonthDayString(job.booking?.start);
    return `${job.availability ?? ''}${start ? `, Starting ${start}` : ''}`.trim();
  }
  return formatDateRange(job.booking?.start, job.booking?.end);
};

const getChildrenLine = (booking?: JobBookingShape): string => {
  if (!booking) return '';
  const n = booking.numberOfChildren ?? 0;
  if (!n) return '';
  const code = BOOKING_TYPE_CODES[booking.typeId ?? 0];
  if (code === 'petCare') return `${n} ${n === 1 ? 'pet' : 'pets'}`;
  if (code === 'seniorCare') return `${n} ${n === 1 ? 'senior' : 'seniors'}`;
  if (code === 'householdServices') return '';
  const ages = booking.kidsAges ? ` (${booking.kidsAges})` : '';
  return `${n} child${n === 1 ? '' : 'ren'}${ages}`;
};

// Angular `badge-service.js` keys its sprite classes off badge.type — we map
// the same keys to the FontAwesome equivalents so they render on the React
// side (we don't ship the legacy SCSS sprite sheet here).
const BADGE_ICON_DEF: Record<string, IconDefinition> = {
  facebook: faFacebook,
  sitter: faUser,
  sitterMember: faIdCard,
  'member-since': faIdCard,
  member: faIdCard,
  'review-received': faComments,
  'review-given': faPenToSquare,
  review: faPenToSquare,
  reviewCount: faComments,
  referrals: faShareNodes,
  acceptsCreditCards: faCreditCard,
  cc: faCreditCard,
  backgroundCheck: faShieldCheck,
  corporate: faBuilding,
  cpr: faHeartPulse,
  family: faHouseUser,
  repeatFamilies: faRotate,
  repeated_sitter: faRotate,
  friend: faUserGroup,
  messageResponse: faMessage,
  response: faClock,
  responseTime: faClock,
  reliable: faCircleCheck,
  trustedBy: faHandshake,
  event: faAward,
};

const SECTION_CARD =
  'border border-neutral-200 bg-white rounded-lg shadow-sm p-4 md:p-6 mb-4';

const isProbablyHeic = (file: File): boolean =>
  file.type === 'image/heic' ||
  file.type === 'image/heif' ||
  /\.(heic|heif)$/i.test(file.name);

const ProfilePhotoUploader = observer(({
  parentId,
  pictureUrl,
  name,
  canEdit,
  onUploaded,
}: {
  parentId?: number;
  pictureUrl?: string;
  name: string;
  canEdit: boolean;
  onUploaded: () => void;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.family);
  const { stores } = useDependencies();
  const modal = useModal();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastPictureUrl = useRef<string | undefined>(stores.user.user?.pictureUrl);

  useEffect(() => {
    const currentUrl = stores.user.user?.pictureUrl;
    if (!canEdit || !currentUrl) return;
    if (currentUrl !== lastPictureUrl.current) {
      lastPictureUrl.current = currentUrl;
      onUploaded();
    }
  }, [stores.user.user?.pictureUrl, canEdit, onUploaded]);

  const openCropper = (objectUrl: string): void => {
    stores.user.setTemporaryPhoto(objectUrl);
    modal?.setModalName(ModalName.Cropper);
  };

  const handleUpload = async (file: File): Promise<void> => {
    if (!parentId) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('pages.parentProfile.photo.tooLarge'));
      return;
    }
    if (!isProbablyHeic(file)) {
      openCropper(URL.createObjectURL(file));
      return;
    }
    let buffer: ArrayBuffer;
    try {
      buffer = await file.arrayBuffer();
    } catch {
      toast.error(t('pages.parentProfile.photo.uploadFailed'));
      return;
    }
    if (!HeicConvertService.isHeic(new Uint8Array(buffer))) {
      openCropper(URL.createObjectURL(file));
      return;
    }
    try {
      const postData = new FormData();
      postData.append('file', new Blob([buffer]));
      const response = (await HeicConvertService.convert(postData)).data.data;
      const base64 = btoa(
        response.data.reduce(
          (res: string, byte: number) => res + String.fromCharCode(byte),
          '',
        ),
      );
      openCropper(`data:image/png;base64,${base64}`);
    } catch (e) {
      if (e instanceof AxiosError && e.response?.data?.error) {
        toast.error(e.response.data.error);
      } else {
        toast.error(t('pages.parentProfile.photo.uploadFailed'));
      }
    }
  };

  const img = (
    <img
      src={pictureUrl}
      alt={name}
      className='w-28 h-28 rounded-full object-cover shadow border-2 border-white'
    />
  );

  if (!canEdit) {
    return <div className='inline-block relative mb-3'>{img}</div>;
  }

  return (
    <div className='inline-block relative mb-3'>
      <Button
        buttonType={ButtonType.tertiary}
        className='block !p-0 !bg-transparent !border-0 cursor-pointer'
        onClick={(): void => fileInputRef.current?.click()}
        aria-label={t('pages.parentProfile.aria.changePhoto')}
      >
        {img}
        <span className='absolute bottom-0 right-0 bg-sapphire text-white rounded-full w-9 h-9 flex items-center justify-center shadow-md border-2 border-white'>
          <FontAwesomeIcon icon={faCamera} />
        </span>
      </Button>
      <input
        ref={fileInputRef}
        type='file'
        accept='image/*, .heic'
        className='sr-only'
        onChange={(e): void => {
          const f = e.target.files?.[0];
          if (f) void handleUpload(f);
          if (e.target) e.target.value = '';
        }}
      />
    </div>
  );
});

const ParentSnippet = ({
  parent,
  canEdit,
  onFlag,
  onShare,
  onBlockToggle,
  viewerIsSitter,
  onPhotoUploaded,
}: {
  parent: ParentExtras;
  canEdit: boolean;
  onFlag: () => void;
  onShare: () => void;
  onBlockToggle: (blocked: boolean) => void;
  viewerIsSitter: boolean;
  onPhotoUploaded: () => void;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.family);
  const kidsCount = (parent.kids ?? []).length;
  const blocked = !!parent.relationship?.blocked;
  return (
    <div className={`${SECTION_CARD} grid grid-cols-1 md:grid-cols-12 gap-3`}>
      <h1 className='md:col-span-3 hidden md:block text-2xl'>
        {parent.firstName}&apos;s Profile
      </h1>
      <div className='md:col-span-6 text-center'>
        <ProfilePhotoUploader
          parentId={parent.id}
          pictureUrl={parent.pictureUrl}
          name={fullName(parent)}
          canEdit={canEdit}
          onUploaded={onPhotoUploaded}
        />
        <h2 className='text-xl font-semibold mb-0'>{parent.name}</h2>
        <div className='text-neutral-700'>
          {parent.address?.city}
          {parent.address?.neighborhood && ' - '}
          {parent.address?.neighborhood}
        </div>
        {kidsCount > 0 && (
          <div className='mb-3'>
            {kidsCount} {kidsCount === 1 ? 'dependent' : 'dependents'}
            {viewerIsSitter && parent.userStats?.lastAccessLabel && (
              <span className='ml-2 lowercase'>
                - <FontAwesomeIcon icon={faBolt} className='mr-1' />
                active {parent.userStats.lastAccessLabel}
              </span>
            )}
          </div>
        )}
        {viewerIsSitter && (
          <div className='inline-flex gap-3 mb-2'>
            <label className='cursor-pointer'>
              <input
                type='radio'
                name='availability-share'
                checked={!blocked}
                onChange={(): void => onBlockToggle(false)}
                className='mr-1'
              />
              Share Availability
            </label>
            <label className='cursor-pointer'>
              <input
                type='radio'
                name='availability-share'
                checked={blocked}
                onChange={(): void => onBlockToggle(true)}
                className='mr-1'
              />
              {t('pages.parentProfile.buttons.hideAvailability')}
            </label>
            <FontAwesomeIcon
              icon={faCircleInfo}
              className='align-middle ml-1 text-neutral-500'
              title={t('pages.parentProfile.tooltips.hideAvailability')}
            />
          </div>
        )}
      </div>
      <div className='md:col-span-3 text-right'>
        {!canEdit && (
          <Button
            buttonType={ButtonType.tertiary}
            id='flag-user'
            onClick={onFlag}
            className='mr-2'
            title={t('pages.parentProfile.aria.report')}
          >
            <FontAwesomeIcon icon={faFlag} className='text-xl' />
          </Button>
        )}
        <Button buttonType={ButtonType.secondary} onClick={onShare}>
          <FontAwesomeIcon icon={faShare} className='mr-1' /> {t('common.share')}
        </Button>
      </div>
    </div>
  );
};

const ParentSittersPreview = ({
  sitters,
  parentId,
}: {
  sitters: Sitter[];
  parentId: number;
}): JSX.Element | null => {
  if (!sitters.length) return null;
  return (
    <a
      href={`/app/parent/${parentId}/booked-sitters`}
      className={`${SECTION_CARD} block hover:bg-neutral-50 no-underline text-neutral-800`}
    >
      <div className='flex justify-between items-center'>
        <h2 className='text-lg font-semibold mb-0'>Booked Caregivers</h2>
        <span className='text-sapphire font-semibold'>See All</span>
      </div>
      <div className='flex gap-2 mt-2'>
        {sitters.map((s) => (
          <img
            key={s.id}
            src={s.pictureUrl}
            alt={`booked-sitter-preview-${s.id}`}
            className='w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm'
          />
        ))}
      </div>
    </a>
  );
};

const ParentOpenJobs = ({
  jobs,
  parent,
}: {
  jobs: Job[];
  parent: ParentExtras;
}): JSX.Element | null => {
  if (!jobs.length) return null;
  return (
    <div className={SECTION_CARD}>
      <h2 className='text-xl font-semibold mb-3'>Open Job Posts</h2>
      <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3'>
        {jobs.map((job) => {
          const booking = job.booking;
          const title = getJobTitle(job);
          const children = getChildrenLine(booking);
          const description = booking?.description?.slice(0, 36);
          const truncated = (booking?.description?.length ?? 0) > 36 ? '...' : '';
          return (
            <a
              key={job.id}
              href={`/app/job/${job.id}`}
              className='block border border-neutral-200 rounded p-3 hover:bg-neutral-50 flex gap-3 no-underline text-neutral-800'
            >
              {parent.pictureUrl && (
                <img
                  src={parent.pictureUrl}
                  alt={parent.firstName ?? ''}
                  className='w-16 h-16 rounded-full object-cover flex-shrink-0'
                />
              )}
              <div className='flex-1 min-w-0'>
                <div className='font-semibold mb-1'>{title || 'Job'}</div>
                {booking?.rate ? (
                  <div className='text-sm text-neutral-600'>
                    ${booking.rate}/hr
                    {booking.paymentType && <span> via {booking.paymentType}</span>}
                  </div>
                ) : null}
                {booking?.address?.city && (
                  <div className='text-sm text-neutral-600'>{booking.address.city}</div>
                )}
                {children && (
                  <div className='text-sm text-neutral-600'>{children}</div>
                )}
                {description && (
                  <div className='text-sm text-neutral-600 mt-1'>
                    {description}
                    {truncated}
                  </div>
                )}
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
};

interface GroupItem {
  id?: number;
  name?: string;
  alias?: string;
}

const ParentBadgesGroups = ({ parent }: { parent: ParentExtras }): JSX.Element => {
  const badges = parent.badges ?? [];
  const local: GroupItem[] = (parent.localGroups ?? []).map((g) => ({
    id: g.id,
    name: g.localGroup?.name,
    alias: (g.localGroup as { alias?: string } | undefined)?.alias,
  }));
  const affiliations: GroupItem[] = (parent.affiliations ?? []).map((g) => ({
    id: g.id,
    name: g.affiliation?.name,
    alias: (g.affiliation as { alias?: string } | undefined)?.alias,
  }));
  const schools: GroupItem[] = (parent.schools ?? []).map((g) => ({
    id: g.id,
    name: g.school?.name,
    alias: (g.school as { alias?: string } | undefined)?.alias,
  }));
  const GroupList = ({
    label,
    items,
    pathSegment,
  }: {
    label: string;
    items: GroupItem[];
    pathSegment: 'local-groups' | 'affiliations' | 'schools';
  }): JSX.Element | null => {
    if (!items.length) return null;
    return (
      <div className='mb-2'>
        <span className='font-semibold mr-1'>{label}:</span>
        {items.map((g, i) => {
          const sep = i < items.length - 1 ? ', ' : '';
          if (g.alias) {
            return (
              <span key={g.id ?? i}>
                <a
                  href={`/app/sitters/${pathSegment}/${g.alias}`}
                  className='text-sapphire hover:underline'
                >
                  {g.name}
                </a>
                {sep}
              </span>
            );
          }
          return (
            <span key={g.id ?? i}>
              {g.name}
              {sep}
            </span>
          );
        })}
      </div>
    );
  };
  return (
    <div className={`${SECTION_CARD} grid grid-cols-1 md:grid-cols-2 md:divide-x md:divide-neutral-200 gap-y-4`}>
      <div className='md:pr-6'>
        <h3 className='font-semibold mb-2 text-lg'>Badges</h3>
        {badges.length === 0 && (
          <p className='text-neutral-500'>No badges yet.</p>
        )}
        <div className='grid grid-cols-1 gap-2'>
          {badges.map((badge, i) => {
            const icon: IconDefinition = badge.type ? BADGE_ICON_DEF[badge.type] ?? faAward : faAward;
            const isActive = badge.active !== false;
            const onClick = (): void => {
              if (badge.url) window.open(badge.url, '_blank');
            };
            return (
              <div
                key={`${badge.type}-${i}`}
                className={`flex items-center gap-3 ${badge.url ? 'cursor-pointer hover:text-sapphire' : ''}`}
                onClick={onClick}
                onKeyDown={(e): void => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick();
                  }
                }}
                role={badge.url ? 'button' : undefined}
                tabIndex={badge.url ? 0 : undefined}
                title={badge.description ?? badge.label}
              >
                <span
                  className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    isActive ? 'bg-sapphire-50 text-sapphire' : 'bg-neutral-100 text-neutral-400'
                  }`}
                >
                  <FontAwesomeIcon icon={icon} className='text-lg' />
                </span>
                <span
                  className={isActive ? '' : 'text-neutral-400'}
                >
                  {badge.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <div className='md:pl-6 border-t border-neutral-200 pt-4 md:border-t-0 md:pt-0'>
        <h3 className='font-semibold mb-2 text-lg'>Groups</h3>
        {local.length + affiliations.length + schools.length === 0 && (
          <p className='text-neutral-500'>No groups yet.</p>
        )}
        <GroupList label='Local Groups' items={local} pathSegment='local-groups' />
        <GroupList label='Affiliations' items={affiliations} pathSegment='affiliations' />
        <GroupList label='Schools' items={schools} pathSegment='schools' />
      </div>
    </div>
  );
};

// Mirrors Angular parent-profile-feedback.seeMore(): the destination depends
// on whether the parent has any reviews and on the rating threshold (80%).
const FeedbackInfoModal = ({
  isOpen,
  onClose,
  parent,
  onSeeReviews,
}: {
  isOpen: boolean;
  onClose: () => void;
  parent: ParentExtras;
  onSeeReviews: () => void;
}): JSX.Element | null => {
  if (!isOpen) return null;
  const rec = parent.recommendation ?? {};
  const reviewCount = rec.reviewCount ?? 0;
  const rating = rec.rating ?? 0;
  const positive = reviewCount > 0 && rating >= 80;
  const negative = reviewCount > 0 && rating < 80;
  const none = reviewCount === 0;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`${parent.firstName}'s Reviews`}
      className='w-full max-w-xl'
      footer={
        <>
          <a
            href='https://support.urbansitter.com/hc/en-us/articles/4404390116251'
            target='_blank'
            rel='noreferrer'
            className='text-sapphire hover:underline text-sm mr-auto self-center'
          >
            Learn more
          </a>
          {!none && (
            <Button buttonType={ButtonType.tertiary} onClick={onSeeReviews}>
              See all reviews
            </Button>
          )}
          <Button buttonType={ButtonType.primary} onClick={onClose}>
            Sounds good
          </Button>
        </>
      }
    >
      {!none && (
        <div className='bg-sapphire-50 rounded p-4 text-center mb-4'>
          <h4 className='font-semibold mb-2'>FYI</h4>
          <div className='mb-1'>
            {rating}% of caregivers recommend working with {parent.firstName}
          </div>
          <div className='text-sm italic'>
            Based on feedback from {reviewCount} caregiver{reviewCount === 1 ? '' : 's'}
          </div>
        </div>
      )}

      {none && (
        <div className='text-center py-3'>
          <p className='mb-3'>{parent.firstName} has no feedback yet.</p>
          <p className='text-sm text-neutral-600'>
            Once caregivers review {parent.firstName}, you&apos;ll see reviews here.
          </p>
        </div>
      )}

      {positive && (
        <>
          <p className='font-semibold mb-2'>
            According to your caregivers, you&apos;re awesome!
          </p>
          <p className='mb-2'>
            Caregivers tell us they prefer working with, and returning to, families that:
          </p>
          <ul className='list-disc list-inside text-sm text-neutral-700 space-y-1 mb-4'>
            <li>Respect their time</li>
            <li>Accurately describe job duties</li>
            <li>Pay based on booking agreements</li>
          </ul>
        </>
      )}

      {negative && (
        <>
          <p className='font-semibold mb-2'>
            Caregivers prefer working with families that:
          </p>
          <ul className='list-disc list-inside text-sm text-neutral-700 space-y-1 mb-3'>
            <li>Respect their time</li>
            <li>Accurately describe job duties</li>
            <li>Pay based on booking agreements</li>
          </ul>
          <p className='text-sm mb-2'>
            If you&apos;ve been reviewed by a caregiver who wasn&apos;t a good fit, don&apos;t worry —
            many caregivers tell us they&apos;ll overlook a single negative review when the job
            otherwise looks like a great match.
          </p>
        </>
      )}

    </Modal>
  );
};

const ParentProfileFeedback = ({
  parent,
  viewerIsParentSelf,
  viewerIsSitter,
  onSeeMore,
}: {
  parent: ParentExtras;
  viewerIsParentSelf: boolean;
  viewerIsSitter: boolean;
  onSeeMore: () => void;
}): JSX.Element => {
  const rec = parent.recommendation ?? {};
  const count = rec.reviewCount ?? 0;
  return (
    <div className={`${SECTION_CARD} grid grid-cols-1 md:grid-cols-12 gap-3`}>
      <div id='sitter-feedback' className='md:col-span-10'>
        <h3 className='font-semibold mb-2 text-lg'>Caregiver Feedback</h3>
        {count === 0 && (
          <div className='text-neutral-500 flex items-center gap-3'>
            <span className='flex items-center justify-center w-10 h-10 rounded-full bg-neutral-100 text-neutral-400'>
              <FontAwesomeIcon icon={faThumbsUp} className='text-lg' />
            </span>
            No recommendations yet
          </div>
        )}
        {count > 0 && (
          <div className='flex items-start gap-3'>
            <span className='flex items-center justify-center w-10 h-10 rounded-full bg-sapphire-50 text-sapphire flex-shrink-0'>
              <FontAwesomeIcon icon={faThumbsUp} className='text-lg' />
            </span>
            <div>
              <div>
                {rec.rating}% of caregivers recommend {parent.firstName}
              </div>
              <div className='italic text-sm text-neutral-600'>
                Based on feedback from {count} caregiver{count === 1 ? '' : 's'}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className='md:col-span-2 text-right'>
        {viewerIsParentSelf && (
          <Button buttonType={ButtonType.secondary} onClick={onSeeMore}>
            See More
          </Button>
        )}
        {viewerIsSitter && (
          <a
            href={`/app/parent/${parent.id}/reviews`}
            className='font-semibold block mt-2 md:mt-10 text-sapphire hover:underline'
          >
            See more
          </a>
        )}
      </div>
    </div>
  );
};

const ParentDetail = ({
  parent,
  canEdit,
  onEditSection,
}: {
  parent: ParentExtras;
  canEdit: boolean;
  onEditSection: (section: string) => void;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.family);
  const [expanded, setExpanded] = useState(true);
  const kids = parent.kids ?? [];
  const languages = parent.languages ?? [];
  const address = parent.address as Partial<{
    street: string;
    street2: string;
    city: string;
    state: string;
    zip: string;
    neighborhood: string;
  }> | undefined;
  const EditBtn = ({ section }: { section: string }): JSX.Element | null =>
    canEdit ? (
      <Button
        buttonType={ButtonType.secondary}
        onClick={(): void => onEditSection(section)}
      >
        Edit
      </Button>
    ) : null;

  const petIconClass = (val: boolean | undefined): string => {
    if (val === true) return 'text-sapphire';
    if (val === false) return 'text-neutral-400';
    return 'text-neutral-300';
  };

  return (
    <>
      <div id='parent-profile-details' className={SECTION_CARD}>
        <div className='grid grid-cols-1 md:grid-cols-12 mb-2'>
          <h3 className='md:col-span-10 font-semibold mb-0 text-lg flex items-center gap-2'>
            <FontAwesomeIcon icon={faLock} />
            Family Details
          </h3>
          <div className='md:col-span-2 text-right'>
            <Button
              buttonType={ButtonType.tertiary}
              onClick={(): void => setExpanded((v) => !v)}
            >
              <FontAwesomeIcon
                icon={expanded ? faChevronUp : faChevronDown}
                className='align-middle'
              />
            </Button>
          </div>
          {canEdit && (
            <div className='md:col-span-10 text-sm text-neutral-600'>
              These details are only shared with caregivers you choose to book
            </div>
          )}
        </div>

        {expanded && (
          <div className='space-y-4'>
            {(canEdit || kids.length > 0) && (
              <>
                <hr className='border-neutral-200' />
                <div className='grid grid-cols-1 md:grid-cols-12 gap-3'>
                  <div id='children' className='md:col-span-10'>
                    <h4 className='font-semibold mb-2'>Dependents</h4>
                    {kids.length === 0 && (
                      <p className='text-neutral-500'>No dependents yet.</p>
                    )}
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                      {kids.map((kid, i) => {
                        const experiences =
                          ((kid as { experiences?: Array<{ specialExperience?: { name?: string } }> }).experiences ?? [])
                            .map((e) => e.specialExperience?.name)
                            .filter(Boolean);
                        return (
                          <div key={kid.id ?? i}>
                            <div className='font-semibold'>{kid.name}</div>
                            {kid.age && <div>Age: {kid.age}</div>}
                            {kid.allergies && <div>Allergies: {kid.allergies}</div>}
                            {experiences.length > 0 && (
                              <div className='text-sm text-neutral-600'>
                                {experiences.join(', ')}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className='md:col-span-2 text-right'>
                    <EditBtn section='children' />
                  </div>
                </div>
              </>
            )}

            {canEdit && (
              <>
                <hr className='border-neutral-200' />
                <div className='grid grid-cols-1 md:grid-cols-12 gap-3'>
                  <div id='home-address' className='md:col-span-10'>
                    <h4 className='font-semibold mb-2'>Home Address</h4>
                    <div>{address?.street}</div>
                    {address?.street2 && <div>{address.street2}</div>}
                    <div>
                      {address?.city}
                      {address?.city && ', '}
                      {address?.state} {address?.zip}
                    </div>
                    {address?.neighborhood && (
                      <div>Neighborhood: {address.neighborhood}</div>
                    )}
                  </div>
                  <div className='md:col-span-2 text-right'>
                    <EditBtn section='address' />
                  </div>
                </div>
              </>
            )}

            <hr className='border-neutral-200' />
            <div className='grid grid-cols-1 md:grid-cols-12 gap-3'>
              <div id='parking' className='md:col-span-10'>
                <h4 className='font-semibold mb-2'>Parking</h4>
                <p>
                  Is there ample parking in your area?{' '}
                  <b>{parent.hasParkingArea ? 'Yes' : 'No'}</b>
                </p>
                <p>
                  Do you have a parking spot that the caregiver can use?{' '}
                  <b>{parent.hasSitterParking ? 'Yes' : 'No'}</b>
                </p>
              </div>
              <div className='md:col-span-2 text-right'>
                <EditBtn section='parking' />
              </div>
            </div>

            <hr className='border-neutral-200' />
            <div className='grid grid-cols-1 md:grid-cols-12 gap-3'>
              <div id='emergency-contacts' className='md:col-span-10'>
                <h4 className='font-semibold mb-2'>Emergency Contacts</h4>
                {(parent.emergencyName || parent.emergencyPhone) && (
                  <div>
                    Emergency contact: {parent.emergencyName}
                    {parent.emergencyName && parent.emergencyPhone && ', '}
                    {parent.emergencyPhone}
                  </div>
                )}
                {(parent.pediatricianName || parent.pediatricianPhone) && (
                  <div>
                    Pediatrician: {parent.pediatricianName}
                    {parent.pediatricianName && parent.pediatricianPhone && ', '}
                    {parent.pediatricianPhone}
                  </div>
                )}
              </div>
              <div className='md:col-span-2 text-right'>
                <EditBtn section='contacts' />
              </div>
            </div>

            <hr className='border-neutral-200' />
            <div className='grid grid-cols-1 md:grid-cols-12 gap-3'>
              <div id='languages' className='md:col-span-10'>
                <h4 className='font-semibold mb-2'>Languages Spoken</h4>
                <div>
                  {languages.length === 0 && (
                    <span className='text-neutral-500'>None specified</span>
                  )}
                  {languages.map((lang, i) => (
                    <span key={lang.id ?? i}>
                      {lang.name}
                      {i < languages.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </div>
              </div>
              <div className='md:col-span-2 text-right'>
                <EditBtn section='languages' />
              </div>
            </div>

            {(canEdit || (parent.schools ?? []).length > 0) && (
              <>
                <hr className='border-neutral-200' />
                <div className='grid grid-cols-1 md:grid-cols-12 gap-3'>
                  <div id='kids-schools' className='md:col-span-10'>
                    <h4 className='font-semibold mb-2'>Schools</h4>
                    <div>
                      {(parent.schools ?? []).map((s, i) => (
                        <span key={s.id ?? i}>
                          {s.school?.name}
                          {i < (parent.schools ?? []).length - 1 ? ', ' : ''}
                        </span>
                      ))}
                      {(parent.schools ?? []).length === 0 && (
                        <span className='text-neutral-500'>None yet</span>
                      )}
                    </div>
                  </div>
                  <div className='md:col-span-2 text-right'>
                    <EditBtn section='schools' />
                  </div>
                </div>
              </>
            )}

            <hr className='border-neutral-200' />
            <div className='grid grid-cols-1 md:grid-cols-12 gap-3'>
              <div id='groups' className='md:col-span-10'>
                <h4 className='font-semibold mb-2'>Groups</h4>
                {(parent.localGroups ?? []).length + (parent.affiliations ?? []).length === 0 && (
                  <span className='text-neutral-500'>None yet</span>
                )}
                {(parent.localGroups ?? []).map((g, i) => (
                  <span key={g.id ?? i}>
                    {g.localGroup?.name}
                    {i < (parent.localGroups ?? []).length - 1 ? ', ' : ''}
                  </span>
                ))}
                {(parent.affiliations ?? []).length > 0 && (parent.localGroups ?? []).length > 0 && (
                  <br />
                )}
                {(parent.affiliations ?? []).map((g, i) => (
                  <span key={g.id ?? i}>
                    {g.affiliation?.name}
                    {i < (parent.affiliations ?? []).length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
              <div className='md:col-span-2 text-right'>
                <EditBtn section='groups' />
              </div>
            </div>

            <hr className='border-neutral-200' />
            <div className='grid grid-cols-1 md:grid-cols-12 gap-3'>
              <div id='pets' className='md:col-span-10'>
                <h4 className='font-semibold mb-2'>{t('common.pets.heading')}</h4>
                <div className='flex gap-6'>
                  <span className='flex items-center gap-2'>
                    <FontAwesomeIcon icon={faDog} className={`text-2xl ${petIconClass(parent.dog)}`} />
                    <span>
                      {parent.dog != null
                        ? t(parent.dog ? 'common.pets.hasDogsCount' : 'common.pets.noDogsCount')
                        : t('common.pets.notSpecifiedDogs')}
                    </span>
                  </span>
                  <span className='flex items-center gap-2'>
                    <FontAwesomeIcon icon={faCat} className={`text-2xl ${petIconClass(parent.cat)}`} />
                    <span>
                      {parent.cat != null
                        ? t(parent.cat ? 'common.pets.hasCatsCount' : 'common.pets.noCatsCount')
                        : t('common.pets.notSpecifiedCats')}
                    </span>
                  </span>
                  <span className='flex items-center gap-2'>
                    <FontAwesomeIcon icon={faPaw} className={`text-2xl ${petIconClass(parent.otherPet)}`} />
                    <span>
                      {parent.otherPet != null
                        ? t(parent.otherPet ? 'common.pets.hasOtherPetsCount' : 'common.pets.noOtherPetsCount')
                        : t('common.pets.notSpecifiedOtherPets')}
                    </span>
                  </span>
                </div>
                {parent.petNote && (
                  <p className='mt-2'>
                    <b>{t('common.pets.notesLabel')} </b>
                    {parent.petNote}
                  </p>
                )}
              </div>
              <div className='md:col-span-2 text-right'>
                <EditBtn section='pets' />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

type EditSection =
  | 'children'
  | 'address'
  | 'parking'
  | 'contacts'
  | 'languages'
  | 'pets'
  | 'schools'
  | 'groups'
  | null;

interface KidForm {
  id?: number;
  name: string;
  year: string;
  month: string;
  allergies: string;
  experienceIds: Set<number>;
}

const parseDob = (dob?: string): { year: string; month: string } => {
  if (!dob) return { year: '', month: '' };
  const m = dob.match(/^(\d{4})-(\d{2})/);
  if (m) return { year: m[1], month: String(Number(m[2])) };
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return { year: '', month: '' };
  return { year: String(d.getFullYear()), month: String(d.getMonth() + 1) };
};

const buildDob = (year: string, month: string): string | undefined => {
  const y = Number(year);
  const m = Number(month);
  if (!y || y < 1900) return undefined;
  const mm = m && m >= 1 && m <= 12 ? m : 1;
  return `${y}-${String(mm).padStart(2, '0')}-01`;
};

const AssociationTypeahead = ({
  label,
  placeholder,
  search,
  onSelect,
}: {
  label: string;
  placeholder: string;
  search: (q: string) => Promise<Array<{ id?: number; name?: string }>>;
  onSelect: (item: { id?: number; name?: string }) => Promise<void>;
}): JSX.Element => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Array<{ id?: number; name?: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      void (async (): Promise<void> => {
        try {
          const list = await search(query.trim());
          setResults(list);
        } catch {
          setResults([]);
        } finally {
          setLoading(false);
        }
      })();
    }, 250);
    return (): void => clearTimeout(timer);
  }, [query, search]);

  const handleSelect = async (item: { id?: number; name?: string }): Promise<void> => {
    setSubmitting(true);
    try {
      await onSelect(item);
      setQuery('');
      setResults([]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='mt-3 mb-3'>
      <label className='block font-semibold mb-1 text-sm'>{label}</label>
      <div className='relative'>
        <FontAwesomeIcon
          icon={faSearch}
          className='absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none'
        />
        <input
          type='text'
          className='form-control w-full border border-neutral-300 rounded pl-9 pr-3 py-2'
          placeholder={placeholder}
          value={query}
          onChange={(e): void => setQuery(e.target.value)}
          disabled={submitting}
        />
        {query.trim().length >= 2 && (
          <div className='absolute z-10 left-0 right-0 bg-white border border-neutral-200 rounded shadow-sm mt-1 max-h-56 overflow-auto'>
            {loading && <div className='px-3 py-2 text-sm text-neutral-500'>Searching…</div>}
            {!loading && results.length === 0 && (
              <div className='px-3 py-2 text-sm text-neutral-500'>No matches.</div>
            )}
            {!loading &&
              results.map((r) => (
                <Button
                  key={r.id}
                  buttonType={ButtonType.tertiary}
                  className='w-full text-left px-3 py-2 hover:bg-neutral-50'
                  disabled={submitting}
                  onClick={(): Promise<void> => handleSelect(r)}
                >
                  {r.name}
                </Button>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

const EditSectionModal = ({
  section,
  parent,
  languages,
  onClose,
  onParentSaved,
  onRemoveAssociation,
  onAddAssociation,
}: {
  section: EditSection;
  parent: ParentExtras;
  languages: Language[];
  onClose: () => void;
  onParentSaved: () => Promise<void>;
  onRemoveAssociation: (
    kind: 'school' | 'affiliation' | 'localGroup',
    id: number,
  ) => Promise<void>;
  onAddAssociation: (
    kind: 'school' | 'affiliation' | 'localGroup',
    entityId: number,
  ) => Promise<void>;
}): JSX.Element | null => {
  const { t } = useSharedTranslation(namespaces.family);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [kidsForms, setKidsForms] = useState<KidForm[]>(() =>
    (parent.kids ?? []).map((k) => {
      const dob = parseDob((k as { dob?: string }).dob);
      const experienceIds = new Set<number>();
      ((k as { experiences?: Array<{ specialExperience?: { id?: number } }> }).experiences ?? [])
        .forEach((e) => {
          if (e.specialExperience?.id) experienceIds.add(e.specialExperience.id);
        });
      return {
        id: k.id,
        name: k.name ?? '',
        year: dob.year,
        month: dob.month,
        allergies: k.allergies ?? '',
        experienceIds,
      };
    }),
  );

  const [removedKidIds, setRemovedKidIds] = useState<number[]>([]);

  const [specialExperiences, setSpecialExperiences] = useState<SpecialExperience[]>([]);
  useEffect(() => {
    if (section !== 'children') return;
    const load = async (): Promise<void> => {
      try {
        const r = await SpecialExperiencesService.getByFilter();
        setSpecialExperiences(r.data?.data ?? []);
      } catch {
        setSpecialExperiences([]);
      }
    };
    void load();
  }, [section]);

  const [addr, setAddr] = useState(() => ({
    street: parent.address?.street ?? '',
    street2: parent.address?.street2 ?? '',
    city: parent.address?.city ?? '',
    state: parent.address?.state ?? '',
    zip: parent.address?.zip ?? '',
    neighborhood: parent.address?.neighborhood ?? '',
  }));

  const [hasParkingArea, setHasParkingArea] = useState(!!parent.hasParkingArea);
  const [hasSitterParking, setHasSitterParking] = useState(!!parent.hasSitterParking);

  const [emergencyName, setEmergencyName] = useState(parent.emergencyName ?? '');
  const [emergencyPhone, setEmergencyPhone] = useState(parent.emergencyPhone ?? '');
  const [pediatricianName, setPediatricianName] = useState(parent.pediatricianName ?? '');
  const [pediatricianPhone, setPediatricianPhone] = useState(parent.pediatricianPhone ?? '');

  const [selectedLanguageIds, setSelectedLanguageIds] = useState<Set<number>>(
    () => new Set(
      (parent.languages ?? [])
        .map((l) => l.languageId ?? l.id)
        .filter((n): n is number => !!n),
    ),
  );

  const [dog, setDog] = useState<boolean | null>(parent.dog ?? null);
  const [cat, setCat] = useState<boolean | null>(parent.cat ?? null);
  const [otherPet, setOtherPet] = useState<boolean | null>(parent.otherPet ?? null);
  const [petNote, setPetNote] = useState(parent.petNote ?? '');

  if (!section) return null;

  const saveKids = async (): Promise<void> => {
    const parentId = parent.id as number;
    await Promise.all(removedKidIds.map((id) => ParentKidsService.delete(id)));
    for (const f of kidsForms) {
      const dob = buildDob(f.year, f.month);
      const experiences = Array.from(f.experienceIds).map((expId) => ({
        specialExperience: { id: expId },
      }));
      const payload = {
        parentId,
        id: f.id,
        name: f.name,
        dob,
        allergies: f.allergies || undefined,
        experiences,
      } as unknown as Parameters<typeof ParentKidsService.create>[0];
      if (f.id) {
        await ParentKidsService.patch(f.id, payload);
      } else {
        await ParentKidsService.create(payload);
      }
    }
  };

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    setError('');
    try {
      if (section === 'children') {
        await saveKids();
        await onParentSaved();
        onClose();
        return;
      }
      if (section === 'languages') {
        const existing = parent.languages ?? [];
        const existingByLangId = new Map<number, number>();
        for (const pl of existing) {
          const langId = pl.languageId ?? pl.id;
          if (langId && pl.id) existingByLangId.set(langId, pl.id);
        }
        const toAdd: number[] = [];
        const toRemove: number[] = [];
        for (const langId of selectedLanguageIds) {
          if (!existingByLangId.has(langId)) toAdd.push(langId);
        }
        for (const [langId, assocId] of existingByLangId.entries()) {
          if (!selectedLanguageIds.has(langId)) toRemove.push(assocId);
        }
        await Promise.all([
          ...toAdd.map((langId) =>
            ParentLanguagesService.create({ parentId: parent.id, languageId: langId }),
          ),
          ...toRemove.map((id) => ParentLanguagesService.delete(id)),
        ]);
        await onParentSaved();
        onClose();
        return;
      }
      if (section === 'schools' || section === 'groups') {
        onClose();
        return;
      }
      let patch: Partial<ParentExtras> = {};
      if (section === 'address') {
        patch = {
          address: {
            street: addr.street,
            street2: addr.street2,
            city: addr.city,
            state: addr.state.toUpperCase(),
            zip: addr.zip,
            neighborhood: addr.neighborhood,
          },
        };
      } else if (section === 'parking') {
        patch = { hasParkingArea, hasSitterParking };
      } else if (section === 'contacts') {
        patch = { emergencyName, emergencyPhone, pediatricianName, pediatricianPhone };
      } else if (section === 'pets') {
        patch = { dog: dog ?? undefined, cat: cat ?? undefined, otherPet: otherPet ?? undefined, petNote };
      }
      await ParentService.patch(parent.id as number, { ...parent, ...patch } as never);
      await onParentSaved();
      onClose();
    } catch {
      setError(t('pages.parentProfile.toasts.removeError'));
    } finally {
      setSubmitting(false);
    }
  };

  const titles: Record<Exclude<EditSection, null>, string> = {
    children: t('pages.parentProfile.sectionTitles.children'),
    address: t('pages.parentProfile.sectionTitles.address'),
    parking: t('pages.parentProfile.sectionTitles.parking'),
    contacts: t('pages.parentProfile.sectionTitles.contacts'),
    languages: t('pages.parentProfile.sectionTitles.languages'),
    pets: t('pages.parentProfile.sectionTitles.pets'),
    schools: t('pages.parentProfile.sectionTitles.schools'),
    groups: t('pages.parentProfile.sectionTitles.groups'),
  };

  const cyclePet = (val: boolean | null): boolean | null => {
    if (val === null) return true;
    if (val === true) return false;
    return null;
  };

  const addKid = (): void => {
    setKidsForms((prev) => [
      ...prev,
      { name: '', year: '', month: '', allergies: '', experienceIds: new Set<number>() },
    ]);
  };
  const removeKid = (i: number): void => {
    setKidsForms((prev) => {
      const target = prev[i];
      if (target?.id) setRemovedKidIds((ids) => [...ids, target.id as number]);
      return prev.filter((_, idx) => idx !== i);
    });
  };
  const updateKid = (i: number, key: 'name' | 'year' | 'month' | 'allergies', val: string): void => {
    setKidsForms((prev) =>
      prev.map((k, idx) => (idx === i ? { ...k, [key]: val } : k)),
    );
  };
  const toggleKidExperience = (i: number, expId: number): void => {
    setKidsForms((prev) =>
      prev.map((k, idx) => {
        if (idx !== i) return k;
        const next = new Set(k.experienceIds);
        if (next.has(expId)) next.delete(expId);
        else next.add(expId);
        return { ...k, experienceIds: next };
      }),
    );
  };

  const petBtnClass = (val: boolean | null): string => {
    if (val === true) return 'bg-sapphire-50 border-sapphire text-sapphire';
    if (val === false) return 'bg-neutral-100 border-neutral-400 text-neutral-500';
    return 'border-neutral-300 text-neutral-500';
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={titles[section]}
      className='w-full max-w-2xl'
      footer={
        section === 'schools' || section === 'groups' ? (
          <Button buttonType={ButtonType.primary} onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button buttonType={ButtonType.tertiary} onClick={onClose}>
              Cancel
            </Button>
            <Button buttonType={ButtonType.primary} disabled={submitting} onClick={submit}>
              Save
            </Button>
          </>
        )
      }
    >
      {section === 'children' && (
        <div>
          {kidsForms.length === 0 && (
            <p className='text-neutral-600 mb-3'>
              No dependents yet. Add one to get started.
            </p>
          )}
          {kidsForms.map((kid, i) => (
            <div key={i} className='border border-neutral-200 rounded p-3 mb-3'>
              <div className='flex justify-between items-start mb-2'>
                <h4 className='font-semibold'>{t('pages.parentProfile.editKid.dependent', { number: i + 1 })}</h4>
                <Button
                  buttonType={ButtonType.tertiary}
                  className='text-sm underline text-red-600'
                  onClick={(): void => removeKid(i)}
                >
                  {t('common.remove')}
                </Button>
              </div>
              <input
                type='text'
                className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-2'
                placeholder={t('common.name')}
                value={kid.name}
                onChange={(e): void => updateKid(i, 'name', e.target.value)}
              />
              <div className='grid grid-cols-2 gap-2 mb-2'>
                <input
                  type='number'
                  min={1}
                  max={12}
                  className='form-control w-full border border-neutral-300 rounded px-3 py-2'
                  placeholder={t('pages.parentProfile.placeholders.birthMonth')}
                  value={kid.month}
                  onChange={(e): void => updateKid(i, 'month', e.target.value)}
                />
                <input
                  type='number'
                  className='form-control w-full border border-neutral-300 rounded px-3 py-2'
                  placeholder={t('pages.parentProfile.placeholders.birthYear')}
                  value={kid.year}
                  onChange={(e): void => updateKid(i, 'year', e.target.value)}
                />
              </div>
              <input
                type='text'
                className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-3'
                placeholder={t('pages.parentProfile.placeholders.allergies')}
                value={kid.allergies}
                onChange={(e): void => updateKid(i, 'allergies', e.target.value)}
              />
              {specialExperiences.length > 0 && (
                <div>
                  <h5 className='font-semibold mb-2'>{t('pages.parentProfile.editKid.additionalConsiderations')}</h5>
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-1'>
                    {specialExperiences.map((exp) => (
                      <label
                        key={exp.id}
                        className='flex items-center gap-2 text-sm cursor-pointer'
                      >
                        <input
                          type='checkbox'
                          checked={!!exp.id && kid.experienceIds.has(exp.id)}
                          onChange={(): void => {
                            if (exp.id) toggleKidExperience(i, exp.id);
                          }}
                        />
                        <span>{exp.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          <Button
            buttonType={ButtonType.secondary}
            className='mb-3'
            onClick={addKid}
          >
            {t('pages.parentProfile.editKid.addAnother')}
          </Button>
        </div>
      )}

      {section === 'address' && (
        <div>
          <div className='grid grid-cols-12 gap-2 mb-2'>
            <input
              type='text'
              className='col-span-7 form-control border border-neutral-300 rounded px-3 py-2'
              placeholder={t('pages.parentProfile.placeholders.street')}
              value={addr.street}
              onChange={(e): void => setAddr((a) => ({ ...a, street: e.target.value }))}
            />
            <input
              type='text'
              className='col-span-5 form-control border border-neutral-300 rounded px-3 py-2'
              placeholder={t('pages.parentProfile.placeholders.apt')}
              value={addr.street2}
              onChange={(e): void => setAddr((a) => ({ ...a, street2: e.target.value }))}
            />
          </div>
          <div className='grid grid-cols-12 gap-2 mb-2'>
            <input
              type='text'
              className='col-span-7 form-control border border-neutral-300 rounded px-3 py-2'
              placeholder={t('pages.parentProfile.placeholders.city')}
              value={addr.city}
              onChange={(e): void => setAddr((a) => ({ ...a, city: e.target.value }))}
            />
            <input
              type='text'
              aria-label={t('pages.parentProfile.aria.state')}
              className='col-span-5 form-control border border-neutral-300 rounded px-3 py-2'
              placeholder={t('pages.parentProfile.placeholders.state')}
              maxLength={2}
              required
              value={addr.state}
              onChange={(e): void => setAddr((a) => ({ ...a, state: e.target.value }))}
            />
          </div>
          <div className='grid grid-cols-12 gap-2 mb-2'>
            <input
              type='tel'
              inputMode='numeric'
              pattern='\d{5}'
              className='col-span-4 form-control border border-neutral-300 rounded px-3 py-2'
              placeholder={t('pages.parentProfile.placeholders.zip')}
              maxLength={5}
              value={addr.zip}
              onChange={(e): void => setAddr((a) => ({ ...a, zip: e.target.value }))}
            />
            <input
              type='text'
              className='col-span-8 form-control border border-neutral-300 rounded px-3 py-2'
              placeholder={t('pages.parentProfile.placeholders.neighborhood')}
              value={addr.neighborhood}
              onChange={(e): void => setAddr((a) => ({ ...a, neighborhood: e.target.value }))}
            />
          </div>
        </div>
      )}

      {section === 'parking' && (
        <div className='space-y-2 mb-4'>
          <label className='flex items-start gap-2'>
            <input
              type='checkbox'
              checked={hasParkingArea}
              onChange={(e): void => setHasParkingArea(e.target.checked)}
            />
            Is there ample parking in your area?
          </label>
          <label className='flex items-start gap-2'>
            <input
              type='checkbox'
              checked={hasSitterParking}
              onChange={(e): void => setHasSitterParking(e.target.checked)}
            />
            Do you have a parking spot that the caregiver can use?
          </label>
        </div>
      )}

      {section === 'contacts' && (
        <div className='space-y-3 mb-4'>
          <div>
            <h4 className='font-semibold mb-1'>{t('pages.parentProfile.contacts.emergencyHeading')}</h4>
            <input
              type='text'
              className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-2'
              placeholder={t('common.name')}
              value={emergencyName}
              onChange={(e): void => setEmergencyName(e.target.value)}
            />
            <input
              type='tel'
              maxLength={12}
              className='form-control w-full border border-neutral-300 rounded px-3 py-2'
              placeholder={t('pages.parentProfile.placeholders.phone')}
              value={emergencyPhone}
              onChange={(e): void => setEmergencyPhone(e.target.value)}
            />
          </div>
          <div>
            <h4 className='font-semibold mb-1'>{t('pages.parentProfile.contacts.pediatricianHeading')}</h4>
            <input
              type='text'
              className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-2'
              placeholder={t('common.name')}
              value={pediatricianName}
              onChange={(e): void => setPediatricianName(e.target.value)}
            />
            <input
              type='tel'
              maxLength={12}
              className='form-control w-full border border-neutral-300 rounded px-3 py-2'
              placeholder={t('pages.parentProfile.placeholders.phone')}
              value={pediatricianPhone}
              onChange={(e): void => setPediatricianPhone(e.target.value)}
            />
          </div>
        </div>
      )}

      {section === 'languages' && (
        <div className='space-y-1 mb-4 max-h-[50vh] overflow-auto'>
          {languages.map((lang) => (
            <label key={lang.id} className='flex items-center gap-2'>
              <input
                type='checkbox'
                checked={lang.id ? selectedLanguageIds.has(lang.id) : false}
                onChange={(): void => {
                  setSelectedLanguageIds((prev) => {
                    const next = new Set(prev);
                    if (lang.id) {
                      if (next.has(lang.id)) next.delete(lang.id);
                      else next.add(lang.id);
                    }
                    return next;
                  });
                }}
              />
              {lang.name}
            </label>
          ))}
        </div>
      )}

      {section === 'pets' && (
        <div className='mb-4'>
          <p className='text-sm mb-3'>{t('common.pets.cycleHint')}</p>
          <div className='flex gap-4 mb-3'>
            <Button
              buttonType={ButtonType.secondary}
              className={`px-4 py-2 rounded border ${petBtnClass(dog)}`}
              onClick={(): void => setDog(cyclePet(dog))}
            >
              <FontAwesomeIcon icon={faDog} className='mr-2' />
              {dog === null
                ? t('common.pets.dogPrompt')
                : dog
                ? t('common.pets.hasDogs')
                : t('common.pets.noDogs')}
            </Button>
            <Button
              buttonType={ButtonType.secondary}
              className={`px-4 py-2 rounded border ${petBtnClass(cat)}`}
              onClick={(): void => setCat(cyclePet(cat))}
            >
              <FontAwesomeIcon icon={faCat} className='mr-2' />
              {cat === null
                ? t('common.pets.catPrompt')
                : cat
                ? t('common.pets.hasCats')
                : t('common.pets.noCats')}
            </Button>
            <Button
              buttonType={ButtonType.secondary}
              className={`px-4 py-2 rounded border ${petBtnClass(otherPet)}`}
              onClick={(): void => setOtherPet(cyclePet(otherPet))}
            >
              <FontAwesomeIcon icon={faPaw} className='mr-2' />
              {otherPet === null
                ? t('common.pets.otherPetPrompt')
                : otherPet
                ? t('common.pets.hasOtherPets')
                : t('common.pets.noOtherPets')}
            </Button>
          </div>
          <textarea
            className='form-control w-full border border-neutral-300 rounded p-2'
            rows={3}
            placeholder={t('pages.parentProfile.placeholders.petDetails')}
            value={petNote}
            onChange={(e): void => setPetNote(e.target.value)}
          />
        </div>
      )}

      {section === 'schools' && (
        <div className='mb-4'>
          {(parent.schools ?? []).length === 0 && (
            <p className='text-neutral-500 mb-2'>
              You aren&apos;t associated with any schools yet.
            </p>
          )}
          {(parent.schools ?? []).map((s) => (
            <div
              key={s.id}
              className='flex items-center justify-between border border-neutral-200 rounded px-3 py-2 mb-2'
            >
              <span>{s.school?.name}</span>
              <Button
                buttonType={ButtonType.tertiary}
                aria-label={`Remove ${s.school?.name ?? 'school'}`}
                className='text-red-600 text-xl leading-none'
                onClick={(): Promise<void> =>
                  s.id != null ? onRemoveAssociation('school', s.id) : Promise.resolve()
                }
              >
                <FontAwesomeIcon icon={faXmark} />
              </Button>
            </div>
          ))}
          <AssociationTypeahead
            label='Add a school'
            placeholder={t('pages.parentProfile.placeholders.searchSchool')}
            search={async (q): Promise<Array<{ id?: number; name?: string }>> => {
              const resp = await SchoolService.getByFilter({ params: { name: q } });
              return (resp.data?.data ?? []) as Array<{ id?: number; name?: string }>;
            }}
            onSelect={(item): Promise<void> =>
              item.id != null ? onAddAssociation('school', item.id) : Promise.resolve()
            }
          />
        </div>
      )}

      {section === 'groups' && (
        <div className='mb-4'>
          {(parent.affiliations ?? []).length + (parent.localGroups ?? []).length === 0 && (
            <p className='text-neutral-500 mb-2'>You aren&apos;t in any groups yet.</p>
          )}
          {(parent.affiliations ?? []).length > 0 && (
            <div className='mb-3'>
              <h4 className='font-semibold mb-1'>Affiliations</h4>
              {(parent.affiliations ?? []).map((a) => (
                <div
                  key={a.id}
                  className='flex items-center justify-between border border-neutral-200 rounded px-3 py-2 mb-2'
                >
                  <span>{a.affiliation?.name}</span>
                  <Button
                    buttonType={ButtonType.tertiary}
                    aria-label={`Remove ${a.affiliation?.name ?? 'affiliation'}`}
                    className='text-red-600 text-xl leading-none'
                    onClick={(): Promise<void> =>
                      a.id != null ? onRemoveAssociation('affiliation', a.id) : Promise.resolve()
                    }
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </Button>
                </div>
              ))}
            </div>
          )}
          <AssociationTypeahead
            label='Add an affiliation'
            placeholder={t('pages.parentProfile.placeholders.searchAffiliations')}
            search={async (q): Promise<Array<{ id?: number; name?: string }>> => {
              const resp = await AffiliationService.getByFilter({ params: { name: q } });
              return (resp.data?.data ?? []) as Array<{ id?: number; name?: string }>;
            }}
            onSelect={(item): Promise<void> =>
              item.id != null ? onAddAssociation('affiliation', item.id) : Promise.resolve()
            }
          />
          {(parent.localGroups ?? []).length > 0 && (
            <div className='mb-3'>
              <h4 className='font-semibold mb-1'>Local Groups</h4>
              {(parent.localGroups ?? []).map((g) => (
                <div
                  key={g.id}
                  className='flex items-center justify-between border border-neutral-200 rounded px-3 py-2 mb-2'
                >
                  <span>{g.localGroup?.name}</span>
                  <Button
                    buttonType={ButtonType.tertiary}
                    aria-label={`Remove ${g.localGroup?.name ?? 'group'}`}
                    className='text-red-600 text-xl leading-none'
                    onClick={(): Promise<void> =>
                      g.id != null ? onRemoveAssociation('localGroup', g.id) : Promise.resolve()
                    }
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </Button>
                </div>
              ))}
            </div>
          )}
          <AssociationTypeahead
            label='Add a local group'
            placeholder={t('pages.parentProfile.placeholders.searchLocalGroups')}
            search={async (q): Promise<Array<{ id?: number; name?: string }>> => {
              const resp = await LocalGroupService.getByFilter({ params: { name: q } });
              return (resp.data?.data ?? []) as Array<{ id?: number; name?: string }>;
            }}
            onSelect={(item): Promise<void> =>
              item.id != null ? onAddAssociation('localGroup', item.id) : Promise.resolve()
            }
          />
        </div>
      )}

      {error && <p className='text-red-600 text-sm mb-2'>{error}</p>}
    </Modal>
  );
};

const FlagContentModal = ({
  isOpen,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reasonId: number, description: string) => Promise<void>;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.family);
  const [reasonId, setReasonId] = useState(0);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setReasonId(0);
      setDescription('');
      setError('');
    }
  }, [isOpen]);

  const submit = async (): Promise<void> => {
    if (!reasonId) {
      setError(t('pages.parentProfile.flagModal.reasonRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(reasonId, description);
      onClose();
    } catch (e) {
      setError(
        e instanceof AxiosError && e.response?.data?.errors?.[0]?.message
          ? e.response.data.errors[0].message
          : t('pages.parentProfile.flagModal.submitError'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.parentProfile.flagModal.title')}
      className='w-full max-w-lg'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button buttonType={ButtonType.primary} disabled={submitting} onClick={submit}>
            {t('pages.parentProfile.flagModal.submit')}
          </Button>
        </>
      }
    >
      <p className='text-sm mb-3'>
        {t('pages.parentProfile.flagModal.intro')}
      </p>
      <fieldset className='mb-3 space-y-1'>
        {PROFILE_FLAG_REASONS.map((r) => (
          <label key={r.key} className='block'>
            <input
              type='radio'
              name='flag-reason'
              checked={reasonId === r.id}
              onChange={(): void => {
                setReasonId(r.id);
                setError('');
              }}
              className='mr-2'
            />
            {t(`pages.parentProfile.flagModal.${r.key}`)}
          </label>
        ))}
      </fieldset>
      <label className='block font-semibold mb-1'>{t('pages.parentProfile.flagModal.details')}</label>
      <textarea
        className='w-full border border-neutral-300 rounded p-2'
        rows={4}
        value={description}
        onChange={(e): void => setDescription(e.target.value)}
      />
      {error && <p className='text-red-600 text-sm mt-2'>{error}</p>}
    </Modal>
  );
};

export const ParentProfile = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.family);
  const { id: pathId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const [parent, setParent] = useState<ParentExtras | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [sitters, setSitters] = useState<Sitter[]>([]);
  const [openJobs, setOpenJobs] = useState<Job[]>([]);
  const [flagOpen, setFlagOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<EditSection>(null);
  const [languages, setLanguages] = useState<Language[]>([]);

  useEffect(() => {
    document.title = 'UrbanSitter - Family Profile';
  }, []);

  useEffect(() => {
    const load = async (): Promise<void> => {
      try {
        const resp = await LanguageService.getByFilter();
        setLanguages((resp.data?.data ?? []) as Language[]);
      } catch {
        setLanguages([]);
      }
    };
    void load();
  }, []);

  const refreshParent = useCallback(async (): Promise<void> => {
    const id = Number(pathId);
    if (!id || !user?.id) return;
    const resp = await ParentService.get(id, {
      params: { fields: 'badges,recommendations', userId: user.id },
    });
    setParent((resp.data?.data ?? null) as ParentExtras | null);
  }, [pathId, user?.id]);

  useEffect(() => {
    const id = Number(pathId);
    if (!id || !user?.id) return;
    const loadParent = async (): Promise<void> => {
      try {
        const resp = await ParentService.get(id, {
          params: { fields: 'badges,recommendations', userId: user.id },
        });
        setParent((resp.data?.data ?? null) as ParentExtras | null);
      } catch (err) {
        console.warn('[ParentProfile] load failed', err);
      } finally {
        setLoaded(true);
      }
    };
    const loadJobs = async (): Promise<void> => {
      try {
        const resp = await JobService.getByFilter({
          params: { parentId: id, status: 'open', sort: 'updated', limit: 6 },
        });
        setOpenJobs((resp.data?.data ?? []) as Job[]);
      } catch {
        setOpenJobs([]);
      }
    };
    void loadParent();
    void loadJobs();

    if (user.parent) {
      void (async (): Promise<void> => {
        try {
          const resp = await SitterService.getByFilter({
            params: { parentId: id, userId: user.id, fields: 'friendRelationship', booked: true },
          });
          setSitters((resp.data?.data ?? []) as Sitter[]);
        } catch {
          setSitters([]);
        }
      })();
    }
  }, [pathId, user?.id, user?.parent]);

  if (!loaded) {
    return (
      <div className='container py-8 text-center'>
        <FontAwesomeIcon icon={faSpinner} spin className='mr-2' />
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  if (!parent?.id) return <div className='container py-8' />;

  const canEdit = !!user?.id && user.id === parent.id;
  const canViewDetails = canEdit || (parent.relationship?.bookingCount ?? 0) > 0;
  const viewerIsParentSelf = canEdit;
  const viewerIsSitter = !!user?.sitter;

  const onFlag = (): void => setFlagOpen(true);
  const onShare = async (): Promise<void> => {
    const shareUrl = window.location.href;
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: `${parent.firstName}'s Profile on UrbanSitter`,
          url: shareUrl,
        });
        return;
      } catch {
        // fall through to copy
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t('pages.parentProfile.toasts.linkCopied'));
    } catch {
      toast.error(t('pages.parentProfile.toasts.shareError'));
    }
  };

  const onBlockToggle = async (blocked: boolean): Promise<void> => {
    try {
      await ParentService.patch(parent.id as number, {
        ...parent,
        relationship: { ...(parent.relationship ?? {}), blocked },
      } as unknown as Parameters<typeof ParentService.patch>[1]);
      toast.success(
        blocked
          ? t('pages.parentProfile.toasts.availabilityHidden')
          : t('pages.parentProfile.toasts.availabilityShared'),
      );
      await refreshParent();
    } catch {
      toast.error(t('pages.parentProfile.toasts.availabilityError'));
    }
  };

  const onFlagSubmit = async (reasonId: number, description: string): Promise<void> => {
    if (!user?.id) return;
    await ContentFlagService.flagProfile({
      flaggedId: parent.id as number,
      flaggingId: user.id,
      description,
      reason: { id: reasonId },
    });
    toast.success(t('pages.parentProfile.toasts.reportSubmitted'));
  };

  const onSeeMoreFeedback = (): void => {
    setFeedbackModalOpen(true);
  };

  const onSeeReviews = (): void => {
    setFeedbackModalOpen(false);
    navigate(`/parent/${parent.id}/reviews`);
  };

  const onEditSection = (section: string): void => {
    setEditingSection(section as EditSection);
  };

  const onParentSaved = async (): Promise<void> => {
    await refreshParent();
    toast.success(t('common.saved'));
  };

  const onRemoveAssociation = async (
    kind: 'school' | 'affiliation' | 'localGroup',
    id: number,
  ): Promise<void> => {
    try {
      if (kind === 'affiliation') {
        await ParentAffiliationService.delete(id);
      } else if (kind === 'localGroup') {
        await ParentLocalGroupsService.delete(id);
      } else {
        await ParentSchoolsService.delete(id);
      }
      await refreshParent();
      toast.success(t('pages.parentProfile.toasts.removed'));
    } catch {
      toast.error(t('pages.parentProfile.toasts.removeError'));
    }
  };

  // Mirrors Angular `parent-school-multi-select.add` /
  // `affiliation-select.add` payload shape: `{ parentId, school: { id } }` —
  // the server rejects flat `schoolId`/`affiliationId`/`localGroupId` keys.
  const onAddAssociation = async (
    kind: 'school' | 'affiliation' | 'localGroup',
    entityId: number,
  ): Promise<void> => {
    if (!parent?.id) return;
    try {
      if (kind === 'school') {
        await ParentSchoolsService.create({
          parentId: parent.id,
          school: { id: entityId },
        });
      } else if (kind === 'affiliation') {
        await ParentAffiliationService.create({
          parentId: parent.id,
          affiliation: { id: entityId },
        });
      } else {
        await ParentLocalGroupsService.create({
          parentId: parent.id,
          localGroup: { id: entityId },
        } as Parameters<typeof ParentLocalGroupsService.create>[0]);
      }
      await refreshParent();
      toast.success(t('pages.parentProfile.toasts.added'));
    } catch {
      toast.error(t('pages.parentProfile.toasts.addError'));
    }
  };

  return (
    <div className='container py-6 max-w-4xl mx-auto' id='parent-profile-page'>
      <ParentSnippet
        parent={parent}
        canEdit={canEdit}
        onFlag={onFlag}
        onShare={onShare}
        onBlockToggle={onBlockToggle}
        viewerIsSitter={viewerIsSitter}
        onPhotoUploaded={refreshParent}
      />

      {user?.parent && sitters.length > 0 && (
        <ParentSittersPreview sitters={sitters} parentId={parent.id} canEdit={canEdit} />
      )}

      <ParentOpenJobs jobs={openJobs} parent={parent} />
      <ParentBadgesGroups parent={parent} />

      <ParentProfileFeedback
        parent={parent}
        viewerIsParentSelf={viewerIsParentSelf}
        viewerIsSitter={viewerIsSitter}
        onSeeMore={onSeeMoreFeedback}
      />

      {canViewDetails && (
        <ParentDetail parent={parent} canEdit={canEdit} onEditSection={onEditSection} />
      )}

      <FlagContentModal
        isOpen={flagOpen}
        onClose={(): void => setFlagOpen(false)}
        onSubmit={onFlagSubmit}
      />

      <FeedbackInfoModal
        isOpen={feedbackModalOpen}
        onClose={(): void => setFeedbackModalOpen(false)}
        parent={parent}
        onSeeReviews={onSeeReviews}
      />

      <EditSectionModal
        // Re-key on section + parent.languages + kids so the modal's local
        // state (selectedLanguageIds, kidsForms, etc.) re-initializes from
        // the latest parent every time it opens.
        key={`${editingSection ?? 'closed'}:${(parent.languages ?? []).map((l) => l.id).join(',')}:${(parent.kids ?? []).map((k) => k.id).join(',')}`}
        section={editingSection}
        parent={parent}
        languages={languages}
        onClose={(): void => setEditingSection(null)}
        onParentSaved={onParentSaved}
        onRemoveAssociation={onRemoveAssociation}
        onAddAssociation={onAddAssociation}
      />
    </div>
  );
});
